import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import datetime from '@sayyesschool/common/libs/datetime';
import TimeZoneSelect from '@sayyesschool/frontend/components/timezone-select';
import UserDomains from '@sayyesschool/frontend/components/user-domains';
import { genderOptions, userDomainOptions } from '@sayyesschool/frontend/features/user';
import { useFormData } from '@sayyesschool/frontend/hooks/form';
import { Button, Flex, Form, IconButton } from '@sayyesschool/frontend/ui-components';

const defaultData = {
    data: {},
    domains: []
};

const getFormData = ({
    firstname = '',
    lastname = '',
    patronym = '',
    email = '',
    gender = '',
    dob = '',
    phone = '',
    phones = [phone],
    timezone = '',
    note = '',
    clientId = '',
    hhid,
    data: {
        hhid: legacyHhid = '',
        address = '',
        occupation = '',
        interests = ''
    } = {},
    domains = ['lk', 'lms']
} = defaultData) => ({
    clientId,
    hhid: hhid ?? legacyHhid,
    firstname,
    lastname,
    patronym,
    email,
    gender,
    dob: dob ? datetime(dob).format('YYYY-MM-DD') : '',
    phone,
    phones: phones.length > 0 ? phones : phone ? [phone] : [''],
    address,
    occupation,
    interests,
    timezone,
    note,
    domains
});

const LearnerForm = forwardRef(({
    learner,
    onChange,
    onSubmit,
    ...props
}, ref) => {
    const formRef = useRef();

    const { data, handleChange } = useFormData(
        getFormData(learner),
        [learner?.id]
    );

    useImperativeHandle(ref, () => ({
        get form() { return formRef.current; },
        get data() { return data; }
    }));

    useEffect(() => {
        onChange?.({
            ...data,
            phones: data.phones.filter(Boolean),
            data: {
                hhid: data.hhid,
                address: data.address,
                occupation: data.occupation,
                interests: data.interests
            }
        });
    }, [data, onChange]);

    const handleSubmit = useCallback(() => {
        onSubmit({
            ...data,
            phones: data.phones.filter(Boolean),
            data: {
                hhid: data.hhid,
                address: data.address,
                occupation: data.occupation,
                interests: data.interests
            }
        });
    }, [data, onSubmit]);

    const addPhone = useCallback(() => {
        const newPhones = [...data.phones, ''];

        handleChange({
            target: {
                name: 'phones',
                value: newPhones
            }
        });
    }, [data.phones, handleChange]);

    const removePhone = useCallback(index => {
        const newPhones = [...data.phones];
        newPhones.splice(index, 1);

        handleChange({
            target: {
                name: 'phones',
                value: newPhones
            }
        });
    }, [data.phones, handleChange]);

    return (
        <Form
            ref={formRef}
            className="LearnerForm"
            onSubmit={handleSubmit}
            {...props}
        >
            <Flex
                dir="column"
                gap="sm"
            >
                <Form.Input
                    name="clientId"
                    value={data.clientId}
                    label="Client ID"
                    type="number"
                    onChange={handleChange}
                />

                <Form.Input
                    name="hhid"
                    value={data.hhid}
                    label="Hollihop ID"
                    placeholder="Чтобы получить id заполните поле Client ID"
                    disabled
                />

                <Form.Input
                    name="firstname"
                    value={data.firstname}
                    label="Имя"
                    required
                    onChange={handleChange}
                />

                <Form.Input
                    name="lastname"
                    value={data.lastname}
                    label="Фамилия"
                    onChange={handleChange}
                />

                <Form.Input
                    name="patronym"
                    value={data.patronym}
                    label="Отчество"
                    onChange={handleChange}
                />

                <Form.Input
                    type="email"
                    name="email"
                    value={data.email}
                    label="Электронная почта"
                    onChange={handleChange}
                />

                {data.phones.map((phone, index) => (
                    <Flex
                        key={index}
                        dir="column"
                        gap="xxs"
                    >
                        <Form.Input
                            type="phone"
                            name={`phones.${index}`}
                            value={phone}
                            label={data.phones.length > 1
                                ? `Телефон ${index + 1}`
                                : 'Телефон'
                            }
                            end={data.phones.length > 1 && (
                                <IconButton
                                    icon="remove"
                                    variant="plain"
                                    title="Удалить телефон"
                                    onClick={() => removePhone(index)}
                                />
                            )}
                            sx={{ width: '100%' }}
                            onChange={handleChange}
                        />

                        {index === data.phones.length - 1 && (
                            <Button
                                icon="add"
                                variant="plain"
                                content="Добавить телефон"
                                size="sm"
                                onClick={addPhone}
                            />
                        )}
                    </Flex>
                ))}

                <Form.RadioGroup
                    name="gender"
                    value={data.gender}
                    label="Пол"
                    items={genderOptions}
                    onChange={handleChange}
                />

                {learner?.id &&
                    <>
                        <Form.Input
                            type="date"
                            name="dob"
                            value={data.dob}
                            label="Дата рождения"
                            onChange={handleChange}
                        />

                        <TimeZoneSelect
                            name="timezone"
                            value={data.timezone}
                            onChange={handleChange}
                        />

                        <Form.Input
                            type="text"
                            name="address"
                            value={data.address}
                            label="Адрес"
                            onChange={handleChange}
                        />

                        <Form.Input
                            type="text"
                            name="occupation"
                            value={data.occupation}
                            label="Род деятельности"
                            onChange={handleChange}
                        />

                        <Form.Input
                            type="text"
                            name="interests"
                            value={data.interests}
                            label="Интересы"
                            onChange={handleChange}
                        />
                    </>
                }

                <Form.Textarea
                    name="note"
                    value={data.note}
                    label="Примечание"
                    resize="auto"
                    onChange={handleChange}
                />

                <Form.Field label="Доступ">
                    <UserDomains
                        domains={userDomainOptions}
                        value={data.domains}
                        onChange={domains =>
                            handleChange({
                                target: {
                                    name: 'domains',
                                    value: domains
                                }
                            })
                        }
                    />
                </Form.Field>
            </Flex>
        </Form>
    );
});

LearnerForm.displayName = 'LearnerForm';

export default LearnerForm;